import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface KnowledgeItem {
  id: string;
  title: string;
  category: string | null;
  legalArea: string | null;
  legalProductId: string | null;
  legalProductName: string | null;
  content: string;
  tags: string | null;
  source: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateKnowledgeItemInput {
  title: string;
  category?: string | null;
  legalArea?: string | null;
  legalProductId?: string | null;
  content: string;
  tags?: string | null;
  source?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

export type UpdateKnowledgeItemInput = CreateKnowledgeItemInput;

export type LoadKnowledgeItemsFilters = {
  includeArchived?: boolean;
  search?: string;
  category?: string;
  legalArea?: string;
  legalProductId?: string;
};

type UseKnowledgeItemsOptions = {
  autoLoad?: boolean;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toBoolean = (value: unknown, defaultValue = true) => {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value).trim().toLowerCase();

  if (['0', 'false', 'inactive', 'inativo', 'archived', 'arquivado', 'nao', 'não'].includes(normalized)) {
    return false;
  }

  if (['1', 'true', 'active', 'ativo', 'sim', 's'].includes(normalized)) {
    return true;
  }

  return Boolean(value);
};

const sortKnowledgeItems = (a: KnowledgeItem, b: KnowledgeItem) =>
  b.updatedAt.localeCompare(a.updatedAt) ||
  b.createdAt.localeCompare(a.createdAt) ||
  a.title.localeCompare(b.title, 'pt-BR');

const normalizationTimestamp = new Date().toISOString();

const normalizeKnowledgeItem = (value: any): KnowledgeItem => ({
  id: String(value?.id ?? ''),
  title: String(value?.title ?? '').trim(),
  category: toNullableText(value?.category),
  legalArea: toNullableText(value?.legalArea ?? value?.legal_area),
  legalProductId: toNullableText(value?.legalProductId ?? value?.legal_product_id),
  legalProductName: toNullableText(value?.legalProductName ?? value?.legal_product_name),
  content: String(value?.content ?? '').trim(),
  tags: toNullableText(value?.tags),
  source: toNullableText(value?.source),
  notes: toNullableText(value?.notes),
  isActive: toBoolean(value?.isActive ?? value?.is_active, true),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? normalizationTimestamp,
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? normalizationTimestamp,
});

const sortAndSetKnowledgeItems = (
  setKnowledgeItems: Dispatch<SetStateAction<KnowledgeItem[]>>,
  next: KnowledgeItem[]
) => {
  setKnowledgeItems([...next].sort(sortKnowledgeItems));
};

export function useKnowledgeItems(options: UseKnowledgeItemsOptions = {}) {
  const { autoLoad = true } = options;
  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadKnowledgeItems = useCallback(async (filters: LoadKnowledgeItemsFilters = {}) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const searchParams = new URLSearchParams();

      if (filters.includeArchived) {
        searchParams.set('includeArchived', '1');
      }

      if (filters.search?.trim()) {
        searchParams.set('search', filters.search.trim());
      }

      if (filters.category?.trim()) {
        searchParams.set('category', filters.category.trim());
      }

      if (filters.legalArea?.trim()) {
        searchParams.set('legalArea', filters.legalArea.trim());
      }

      if (filters.legalProductId?.trim()) {
        searchParams.set('legalProductId', filters.legalProductId.trim());
      }

      const endpoint = searchParams.toString()
        ? `/api/knowledge-items?${searchParams.toString()}`
        : '/api/knowledge-items';

      const response = await fetchWithRetry(endpoint);
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.knowledgeItems)) {
        throw new Error(data.error || `GET /api/knowledge-items falhou com status ${response.status}`);
      }

      if (requestId === requestIdRef.current) {
        sortAndSetKnowledgeItems(setKnowledgeItems, data.knowledgeItems.map(normalizeKnowledgeItem));
      }
    } catch (error) {
      console.error('Erro ao carregar itens de conhecimento reais. ', error);

      if (requestId === requestIdRef.current) {
        setKnowledgeItems([]);
        setError('Não foi possível carregar os dados reais. Tente novamente.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const createKnowledgeItem = useCallback(async (input: CreateKnowledgeItemInput) => {
    const response = await fetch('/api/knowledge-items', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.knowledgeItem) {
      throw new Error(data.error || `POST /api/knowledge-items falhou com status ${response.status}`);
    }

    const created = normalizeKnowledgeItem(data.knowledgeItem);
    setKnowledgeItems((prev) => [created, ...prev.filter((item) => item.id !== created.id)].sort(sortKnowledgeItems));
    return created;
  }, []);

  const updateKnowledgeItem = useCallback(async (id: string, input: UpdateKnowledgeItemInput) => {
    const response = await fetch(`/api/knowledge-items/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.knowledgeItem) {
      throw new Error(data.error || `PUT /api/knowledge-items/${id} falhou com status ${response.status}`);
    }

    const updated = normalizeKnowledgeItem(data.knowledgeItem);
    setKnowledgeItems((prev) => [updated, ...prev.filter((item) => item.id !== updated.id)].sort(sortKnowledgeItems));
    return updated;
  }, []);

  const archiveKnowledgeItem = useCallback(async (id: string) => {
    const response = await fetch(`/api/knowledge-items/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `DELETE /api/knowledge-items/${id} falhou com status ${response.status}`);
    }

    setKnowledgeItems((prev) => prev.filter((item) => item.id !== id));
    return true;
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadKnowledgeItems();
  }, [autoLoad, loadKnowledgeItems]);

  return {
    knowledgeItems,
    loading,
    error,
    refetch: loadKnowledgeItems,
    createKnowledgeItem,
    updateKnowledgeItem,
    archiveKnowledgeItem,
  };
}
